import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { GlobalApplicationState } from "globalApplicationState";
import ContentView from "modules/common/components/authoring/contentView/contentView";
import { ContentType } from "modules/common/models";
import { Answers, AttendanceType, EventView } from "modules/events/models";
import { useSelector } from "react-redux";
import EventTypeBanner from "modules/common/components/banners/eventTypeBanner";
import DateBlock from "../date-block/dateBlock";
import Location from "./components/location";
import EventTimes from "./components/eventTimes";
import CutoffTime from "./components/cutoffTime";
import Topics from "modules/posts/components/post-feed/components/topics/topics";
import { ActionButtonsContainer } from "../../../common/components/actionButtons/actionButtonsContainer";
import AddToCalendar from "./components/addToCalendar";
import Attendance from "../attendance/attendance";
import { getTranslatedTitle } from "utils/getTranslatedContent";
import { ActionButtons } from "../../../common/components/actionButtons/actionButtons";
import SignUpForm from "./components/signUpForm";
import ShareButtonv2 from "modules/common/components/buttons/shareButtonv2";
import InternalShareScreen from "modules/sharing/internalShareScreen";
import { ContentType as ShareContentType } from "modules/sharing/models";

interface IEventViewContentProps {
    event: EventView;
    onSignUp: (attendanceType: AttendanceType, answers: Answers) => Promise<EventView>;
}

export const EventViewContent: React.FC<IEventViewContentProps> = ({
    event,
    onSignUp
}) => {
    const defaultLCID = useSelector((state: GlobalApplicationState) => state.settings.currentUser.preferredLCID);
    const tenantName = useSelector((state: GlobalApplicationState) => state.settings.tenantSettings.displayName);
    const [activeLCID, setActiveLCID] = useState<string>(defaultLCID);
    const [showSignUpOptions, setShowSignUpOptions] = useState<boolean>(false);
    const eventStartTimes = Object.keys(event.eventTimes!)[0];
    const { currentUserResponse, respondingEnabled } = event || {};
    const title = getTranslatedTitle(event.translatedContent, activeLCID);
    const [internalShareOpen, setInternalShareOpen] = useState<boolean>(false);

    // do not show download ics button if person has responded not attending
    const showAddToCalendar = !(respondingEnabled && currentUserResponse?.attendanceType === "NotAttending");

    return <>
        <div className="event-views event-view">
            <ContentView
                contentType={ContentType.Event}
                content={event}
                heroBannerChildren={
                    <>
                        <EventTypeBanner eventType={event.eventType} isRestricted={event.isRestricted} full lcid={activeLCID}/>
                        <DateBlock
                            eventStartTime={eventStartTimes}
                            eventEndTime={event.eventTimes![eventStartTimes]}
                            eventTimes={event.eventTimes}
                        />
                    </>
                }
                pageContentInfoComponent={
                    <>
                        <Location location={event.location} locationLink={event.locationLink} locationLinkLabel={event.locationLinkLabel} />
                        <EventTimes
                            activeLcid={activeLCID}
                            eventTimes={event.eventTimes!}
                            translatedContent={event.translatedContent!}
                        />
                        <CutoffTime cutoffTime={event.cutoffTime} eventType={event.eventType!} />
                        {
                            event.tags && event.tags.length &&
                            <Topics topics={event.tags} className="event-content-view-tags" iconSize="small" iconClassName="event-content-view-tags-icon"/>
                        }
                    </>
                }
                onChangeActiveLcid={(activeLcid) => setActiveLCID(activeLcid)}
                bottomPageContentComponent={
                    <div className="comments">
                        <div>
                            <FormattedMessage id="events.commentsNotAvailable" defaultMessage="Comments are not available for events yet." />
                        </div>
                    </div>
                }
            />
        </div>
        <ActionButtonsContainer
            showExtraOptions={showSignUpOptions}
            leftBarComponent={
                <>
                    {showAddToCalendar && <AddToCalendar activeLcid={activeLCID} eventId={event.id} {...event} />}
                    {
                        <>
                            <ShareButtonv2
                                isPublic={false} //events cannot be public.
                                isAuthenticated
                                toggleInternalShare={(toSet) => setInternalShareOpen(toSet)}
                            />
                            {
                                internalShareOpen &&
                                <InternalShareScreen 
                                    onCloseDialog={() => setInternalShareOpen(false)}
                                    tenantName={tenantName}
                                    contentTitle={title}
                                    contentId={event?.id ?? ""}
                                    contentStyling={{marginTop: event?.imageUrl === "" ? "5%" : "35%"}}
                                    contentType={ShareContentType.Event}
                                    isPublic={false}
                                />
                            }
                        </>
                    }
                    <Attendance {...event} />
                </>
            }
            actionButtonsComponent={<ActionButtons {...event} onPrimaryButtonClick={() => setShowSignUpOptions(true)}/>}
            extraOptionsComponent={
                <SignUpForm 
                    {...event} 
                    title={title} 
                    onSignUp={onSignUp} 
                    onClose={() => setShowSignUpOptions(false)} 
                />
            }
        />
    </>;
};
